import React, { useState } from 'react';
import styles from './ComponentShowcase.module.css';

// Import all components
import Button from './components/Button.js';
import Charcode from './components/Charcode.js';
import CharcodePill from './components/CharcodePill.js';
import CharcodeTag from './components/CharcodeTag.js';
import CharcodeTooltip from './components/CharcodeTooltip.js';
import CharpointsBanner from './components/CharpointsBanner.js';
import Checkbox from './components/Checkbox.js';
import DateSelector from './components/DateSelector.js';
import DottedLine from './components/DottedLine.js';
import EventPopup from './components/EventPopup.js';
import Icon from './components/Icon.js';
import Incrementer from './components/Incrementer.js';
import KeyFigures from './components/KeyFigures.js';
import LoaderAnimation from './components/LoaderAnimation.js';
import Module from './components/Module.js';
import Nav from './components/Nav.js';
import Overlay from './components/Overlay.js';
import QRCodeScanner from './components/QRCodeScanner.js';
import ScreenHeader from './components/ScreenHeader.js';
import Selector from './components/Selector.js';
import Slider from './components/Slider.js';
import TagSelector from './components/TagSelector.js';
import TextInput from './components/TextInput.js';
import ToggleSwitch from './components/ToggleSwitch.js';
import Tooltip from './components/Tooltip.js';
import WeekPopup from './components/WeekPopup.js';
import ComingUp from './components/subscription/ComingUp.js';
import DetailsTable from './components/subscription/DetailsTable.js';
import Gauge from './components/subscription/Gauge.js';
import ApplicationCard from './components/zones/ApplicationCard.js';
import ProductCounter from './components/subscription/ProductCounter.js';

const ComponentShowcase = () => {
  const [currentComponentIndex, setCurrentComponentIndex] = useState(0);
  const goToIndex = (nextIndex) => {
    setCurrentComponentIndex((prev) => {
      if (nextIndex < 0) return 0;
      if (nextIndex >= components.length) return components.length - 1;
      return nextIndex;
    });
  };

  // Component definitions with descriptions, props, and default values
  const components = [
    {
      name: 'ApplicationCard',
      description: 'Expandable card displaying application details with bag IDs and targeted zones.',
      component: ApplicationCard,
      props: {
        app: { 
          type: 'object', 
          default: { 
            id: 'A-001',
            date: '13-02-2025',
            quantity: 3,
            productType: 'PelletChar',
            weight: 650.8,
            deliveryMethod: 'Direct-to-Land',
            targetedZones: ['Field A', 'Field B'],
            bagIds: ['CHA-000130', 'CHA-000131', 'CHA-000132']
          }, 
          label: 'app' 
        },
        isPlaceholder: { type: 'boolean', default: false, label: 'isPlaceholder' }
      }
    },
    {
      name: 'Button',
      description: 'Primary CTA with optional react-icons adornments and selected state styling.',
      component: Button,
      props: {
        name: { type: 'string', default: 'Click Me', label: 'name' },
        nameSelected: { type: 'string', default: 'Selected', label: 'nameSelected' },
        selected: { type: 'boolean', default: false, label: 'selected' },
        color: { type: 'enum', default: 'Leaf', options: ['Leaf', 'Coal', 'Grass', 'Mint'], label: 'color' },
        disabled: { type: 'boolean', default: false, label: 'disabled' },
        onPress: { type: 'function', default: () => console.log('Button pressed'), label: 'onPress' },
        customStyle: { type: 'object', default: {}, label: 'customStyle' },
        iconName: { type: 'string', default: 'FaHome', label: 'iconName' },
        iconSize: { type: 'number', default: 18, min: 8, max: 48, label: 'iconSize' },
        iconPosition: { type: 'enum', default: 'left', options: ['left', 'right'], label: 'iconPosition' }
      }
    },
    {
      name: 'Charcode',
      description: 'Generates the branded QR card (plus optional tooltip-only mode) for a single bag.',
      component: Charcode,
      props: {
        charcodeId: { type: 'string', default: 'CHA-000000', label: 'charcodeId' },
        isTooltip: { type: 'boolean', default: false, label: 'isTooltip' }
      }
    },
    {
      name: 'CharcodePill',
      description: 'Status pill showing #CHA-XXXXXX plus hover tooltip support.',
      component: CharcodePill,
      props: {
        charcodeId: { type: 'string', default: 'CHA-002428', label: 'charcodeId' },
        bagInfo: { type: 'object', default: null, label: 'bagInfo' },
        size: { type: 'enum', default: 'medium', options: ['small', 'medium', 'large'], label: 'size' }
      }
    },
    {
      name: 'CharcodeTag',
      description: 'Lightweight tag variant that still triggers the tooltip on hover.',
      component: CharcodeTag,
      props: {
        charcodeId: { type: 'string', default: 'CHA-002428', label: 'charcodeId' }
      }
    },
    {
      name: 'CharcodeTooltip',
      description: 'Fetch-independent tooltip shell that renders the farmer, status, delivery and application metadata you pass in.',
      component: CharcodeTooltip,
      props: {
        charcodeId: { type: 'string', default: 'CHA-002428', label: 'charcodeId' },
        bagInfo: { type: 'object', default: { farmer: 'John Doe', status: 'Delivered', site: 'Field A' }, label: 'bagInfo' },
        position: { type: 'object', default: { x: 100, y: 100 }, label: 'position' },
        displayTip: { type: 'boolean', default: true, label: 'displayTip' },
        minWidth: { type: 'string', default: '200px', label: 'minWidth' },
        maxWidth: { type: 'string', default: '400px', label: 'maxWidth' }
      }
    },
    {
      name: 'CharpointsBanner',
      description: 'Highlights the current Charpoints total (value derived from numberOfBags * £10).',
      component: CharpointsBanner,
      props: {
        numberOfBags: { type: 'number', default: 5, min: 0, max: 100, label: 'numberOfBags' }
      }
    },
    {
      name: 'Checkbox',
      description: 'Custom checkbox + label row.',
      component: Checkbox,
      props: {
        text: { type: 'string', default: 'Checkbox Label', label: 'text' },
        checked: { type: 'boolean', default: false, label: 'checked' },
        onPress: { type: 'function', default: () => console.log('Checkbox toggled'), label: 'onPress' },
        inverted: { type: 'boolean', default: false, label: 'inverted' },
        thinnerText: { type: 'boolean', default: false, label: 'thinnerText' },
        customStyle: { type: 'object', default: {}, label: 'customStyle' }
      }
    },
    {
      name: 'ComingUp',
      description: 'Displays upcoming events (deliveries, invoices) in a calendar-style layout.',
      component: ComingUp,
      props: {
        events: { 
          type: 'array', 
          default: [
            { type: 'order', date: new Date().toISOString(), pincharBags: 20 },
            { type: 'invoice', date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), amountDue: 500 }
          ], 
          label: 'events' 
        }
      }
    },
    {
      name: 'DateSelector',
      description: 'TextInput wrapper that renders an HTML type="date" control with the same label treatments.',
      component: DateSelector,
      props: {
        name: { type: 'string', default: 'Select Date', label: 'name' },
        value: { type: 'string', default: '', label: 'value' },
        onChange: { type: 'function', default: (val) => console.log('Date changed:', val), label: 'onChange' },
        placeholder: { type: 'string', default: 'YYYY-MM-DD', label: 'placeholder' },
        labelStyle: { type: 'enum', default: 'top', options: ['left', 'top'], label: 'labelStyle' },
        iconName: { type: 'string', default: '', label: 'iconName' },
        iconCustom: { type: 'string', default: '', label: 'iconCustom' },
        height: { type: 'string', default: '', label: 'height' },
        customStyle: { type: 'object', default: {}, label: 'customStyle' },
        highlighted: { type: 'boolean', default: false, label: 'highlighted' }
      }
    },
    {
      name: 'DetailsTable',
      description: 'Subscription details table displaying farm information, order status, and delivery metrics.',
      component: DetailsTable,
      props: {
        user: { 
          type: 'object', 
          default: { 
            details: { businessName: "John's Dairy Farm" } 
          }, 
          label: 'user' 
        },
        subscription: { 
          type: 'object', 
          default: { 
            status: 'active',
            created_at: new Date().toISOString(),
            contract_plan: 'subscription',
            products: [{ amount_to_deliver: 50 }],
            payment_status: 'paid',
            deliveries_complete: 3
          }, 
          label: 'subscription' 
        }
      }
    },
    {
      name: 'DottedLine',
      description: 'Vertical stack of rectangular "segments" used for visual dividers.',
      component: DottedLine,
      props: {
        segmentNumber: { type: 'number', default: 10, min: 1, max: 50, label: 'segmentNumber' },
        segmentHeight: { type: 'string', default: '0.2rem', label: 'segmentHeight' },
        segmentWidth: { type: 'string', default: '0.7rem', label: 'segmentWidth' },
        color: { type: 'string', default: 'grass', label: 'color' }
      }
    },
    {
      name: 'EventPopup',
      description: 'Fullscreen admin overlay for logging orders/events.',
      component: EventPopup,
      props: {
        displayEventPopup: { type: 'boolean', default: false, label: 'displayEventPopup' },
        closePopup: { type: 'function', default: () => console.log('Close popup'), label: 'closePopup' },
        editingEvent: { type: 'boolean', default: false, label: 'editingEvent' }
      }
    },
    {
      name: 'Gauge',
      description: 'Circular progress gauge displaying completion ratio (e.g., bags delivered vs. total).',
      component: Gauge,
      props: {
        progress: { type: 'number', default: 25, min: 0, max: 100, label: 'progress' },
        progressMax: { type: 'number', default: 100, min: 0, max: 1000, label: 'progressMax' },
        unit: { type: 'string', default: 'Bags', label: 'unit' }
      }
    },
    {
      name: 'Icon',
      description: 'Wrapper over react-icons (FA, FA6, HI, IO sets) so you can reference icons by name.',
      component: Icon,
      props: {
        name: { type: 'string', default: 'FaHome', label: 'name' },
        size: { type: 'number', default: 16, min: 8, max: 64, label: 'size' },
        color: { type: 'string', default: '#000000', label: 'color' },
        className: { type: 'string', default: '', label: 'className' },
        style: { type: 'object', default: {}, label: 'style' },
        onClick: { type: 'function', default: () => console.log('Icon clicked'), label: 'onClick' }
      }
    },
    {
      name: 'Incrementer',
      description: 'Numeric stepper for "number of bags" style inputs.',
      component: Incrementer,
      props: {
        value: { type: 'number', default: 5, min: 0, max: 100, label: 'value' },
        fieldName: { type: 'string', default: 'bags', label: 'fieldName' },
        handleChange: { type: 'function', default: (name, val) => console.log('Changed:', name, val), label: 'handleChange' },
        increment: { type: 'number', default: 1, min: 1, max: 10, label: 'increment' },
        minValue: { type: 'number', default: 0, min: 0, max: 100, label: 'minValue' },
        maxValue: { type: 'number', default: 100, min: 0, max: 1000, label: 'maxValue' }
      }
    },
    {
      name: 'KeyFigures',
      description: 'Horizontal metric list used on dashboards.',
      component: KeyFigures,
      props: {
        data: { 
          type: 'array', 
          default: [
            { label: 'Total Bags', value: 150, prefix: '', unit: '' },
            { label: 'Charpoints', value: 1500, prefix: '£', unit: '' },
            { label: 'Active Zones', value: 5, prefix: '', unit: '' }
          ], 
          label: 'data' 
        }
      }
    },
    {
      name: 'LoaderAnimation',
      description: 'Placeholder file reserved for a bespoke loading animation (no implementation/props yet).',
      component: LoaderAnimation,
      props: {}
    },
    {
      name: 'Module',
      description: 'Responsive card/grid tile with optional iconography.',
      component: Module,
      props: {
        name: { type: 'string', default: 'Module Title', label: 'name' },
        children: { type: 'string', default: 'Module content goes here', label: 'children' },
        spanRow: { type: 'number', default: 1, min: 1, max: 5, label: 'spanRow' },
        spanColumn: { type: 'number', default: 1, min: 1, max: 5, label: 'spanColumn' },
        height: { type: 'string', default: '100%', label: 'height' },
        background: { type: 'string', default: '', label: 'background' },
        icon: { type: 'string', default: '📦', label: 'icon' },
        iconSize: { type: 'number', default: 24, min: 8, max: 64, label: 'iconSize' }
      }
    },
    {
      name: 'Nav',
      description: 'Left-hand navigation rail with admin vs farmer menu sets.',
      component: Nav,
      props: {
        currentScreen: { type: 'string', default: 'home', label: 'currentScreen' },
        goToScreen: { type: 'function', default: (path) => console.log('Navigate to:', path), label: 'goToScreen' },
        isAdmin: { type: 'boolean', default: false, label: 'isAdmin' }
      }
    },
    {
      name: 'Overlay',
      description: 'Generic dimmed background + centered container. Simply wraps props.children.',
      component: Overlay,
      props: {
        children: { type: 'string', default: 'Overlay Content', label: 'children' }
      }
    },
    {
      name: 'ProductCounter',
      description: 'Product selection counter with volume and weight calculations for subscription orders.',
      component: ProductCounter,
      props: {
        name: { type: 'string', default: 'Pinchar', label: 'name' },
        selectedProduct: { type: 'string', default: '', label: 'selectedProduct' },
        handleProductSelection: { type: 'function', default: (name) => console.log('Select product:', name), label: 'handleProductSelection' },
        disabled: { type: 'boolean', default: false, label: 'disabled' },
        perBag: { type: 'string', default: '£10.00', label: 'perBag' },
        fieldName: { type: 'string', default: 'pinchar', label: 'fieldName' },
        handleChange: { type: 'function', default: (name, val) => console.log('Change:', name, val), label: 'handleChange' },
        value: { type: 'number', default: 0, min: 0, max: 1000, label: 'value' }
      }
    },
    {
      name: 'QRCodeScanner',
      description: 'Html5-qrcode based scanner with manual entry fallback and bag list.',
      component: QRCodeScanner,
      props: {
        scanResults: { type: 'array', default: [], label: 'scanResults' },
        addCharcode: { type: 'function', default: (code) => console.log('Add:', code), label: 'addCharcode' },
        removeCharcode: { type: 'function', default: (code) => console.log('Remove:', code), label: 'removeCharcode' },
        totalBagsExpected: { type: 'number', default: 10, min: 0, max: 100, label: 'totalBagsExpected' },
        closeScanner: { type: 'function', default: () => console.log('Close scanner'), label: 'closeScanner' }
      }
    },
    {
      name: 'ScreenHeader',
      description: 'Simple h2/h3 header used on farmer flows.',
      component: ScreenHeader,
      props: {
        name: { type: 'string', default: 'Screen Title', label: 'name' },
        content: { type: 'string', default: 'Supporting subtitle text', label: 'content' }
      }
    },
    {
      name: 'Selector',
      description: 'Custom dropdown with optional icon, label positions, and white/coal variants.',
      component: Selector,
      props: {
        name: { type: 'string', default: 'Select Option', label: 'name' },
        value: { type: 'string', default: '', label: 'value' },
        onChange: { type: 'function', default: (val) => console.log('Selected:', val), label: 'onChange' },
        data: { 
          type: 'array', 
          default: [
            { name: 'Option 1', value: 'opt1' },
            { name: 'Option 2', value: 'opt2' },
            { name: 'Option 3', value: 'opt3' }
          ], 
          label: 'data' 
        },
        placeholder: { type: 'string', default: 'Choose an option...', label: 'placeholder' },
        labelStyle: { type: 'enum', default: 'top', options: ['left', 'top', 'none'], label: 'labelStyle' },
        iconName: { type: 'string', default: '', label: 'iconName' },
        color: { type: 'enum', default: 'white', options: ['white', 'coal'], label: 'color' },
        customStyle: { type: 'object', default: {}, label: 'customStyle' }
      }
    },
    {
      name: 'Slider',
      description: 'Range slider used in calculators (labels change based on name).',
      component: Slider,
      props: {
        name: { type: 'enum', default: 'beddingArea', options: ['beddingArea', 'beddingPeriod', 'manureQty', 'slurryQty'], label: 'name' },
        value: { type: 'number', default: 50, min: 0, max: 100, label: 'value' },
        handleChange: { type: 'function', default: (name, val) => console.log('Slider:', name, val), label: 'handleChange' },
        minRange: { type: 'number', default: 0, min: 0, max: 1000, label: 'minRange' },
        maxRange: { type: 'number', default: 100, min: 0, max: 1000, label: 'maxRange' }
      }
    },
    {
      name: 'TagSelector',
      description: 'Multi-select tag cloud.',
      component: TagSelector,
      props: {
        tags: { 
          type: 'array', 
          default: ['Tag 1', 'Tag 2', 'Tag 3', 'Tag 4', 'Tag 5'], 
          label: 'tags' 
        },
        selectedTags: { type: 'array', default: [], label: 'selectedTags' },
        updateTags: { type: 'function', default: (tag) => console.log('Tag toggled:', tag), label: 'updateTags' },
        name: { type: 'string', default: 'Select Tags', label: 'name' }
      }
    },
    {
      name: 'TextInput',
      description: 'Core text/textarea input used everywhere.',
      component: TextInput,
      props: {
        name: { type: 'string', default: 'Input Label', label: 'name' },
        value: { type: 'string', default: '', label: 'value' },
        onChange: { type: 'function', default: (val) => console.log('Input changed:', val), label: 'onChange' },
        placeholder: { type: 'string', default: 'Enter text...', label: 'placeholder' },
        labelStyle: { type: 'enum', default: 'top', options: ['left', 'top'], label: 'labelStyle' },
        iconName: { type: 'string', default: '', label: 'iconName' },
        iconCustom: { type: 'string', default: '', label: 'iconCustom' },
        isPassword: { type: 'boolean', default: false, label: 'isPassword' },
        highlighted: { type: 'boolean', default: false, label: 'highlighted' },
        height: { type: 'string', default: '', label: 'height' },
        customStyle: { type: 'object', default: {}, label: 'customStyle' },
        onKeyDown: { type: 'function', default: () => {}, label: 'onKeyDown' }
      }
    },
    {
      name: 'ToggleSwitch',
      description: 'iOS-style toggle.',
      component: ToggleSwitch,
      props: {
        toggled: { type: 'boolean', default: false, label: 'toggled' },
        onPress: { type: 'function', default: () => console.log('Toggle pressed'), label: 'onPress' }
      }
    },
    {
      name: 'Tooltip',
      description: 'Generic tooltip bubble for explanatory copy.',
      component: Tooltip,
      props: {
        tip: { type: 'string', default: 'This is a helpful tooltip message', label: 'tip' },
        type: { type: 'enum', default: 'i', options: ['i', '?'], label: 'type' },
        size: { type: 'enum', default: 'medium', options: ['small', 'medium', 'large'], label: 'size' },
        direction: { type: 'enum', default: 'topRight', options: ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'], label: 'direction' },
        minWidth: { type: 'string', default: '', label: 'minWidth' },
        maxWidth: { type: 'string', default: '', label: 'maxWidth' }
      }
    },
    {
      name: 'WeekPopup',
      description: 'Timeline/week planner overlay.',
      component: WeekPopup,
      props: {
        displayWeekPopup: { type: 'boolean', default: false, label: 'displayWeekPopup' },
        closePopup: { type: 'function', default: () => console.log('Close popup'), label: 'closePopup' },
        displayedWeek: { type: 'object', default: { events: [], notes: '', startDate: new Date().toISOString() }, label: 'displayedWeek' },
        weekSlots: { type: 'array', default: [], label: 'weekSlots' },
        setDisplayedWeek: { type: 'function', default: (week) => console.log('Set week:', week), label: 'setDisplayedWeek' },
        editingEvent: { type: 'boolean', default: false, label: 'editingEvent' }
      }
    }
  ];

  const [componentStates, setComponentStates] = useState(() => {
    const states = {};
    components.forEach(comp => {
      states[comp.name] = {};
      Object.keys(comp.props).forEach(propKey => {
        const prop = comp.props[propKey];
        states[comp.name][propKey] = prop.default;
      });
    });
    return states;
  });

  const updateProp = (componentName, propKey, value) => {
    setComponentStates(prev => ({
      ...prev,
      [componentName]: {
        ...prev[componentName],
        [propKey]: value
      }
    }));
  };

  const renderPropEditor = (componentName, propKey, propDef) => {
    const currentValue = componentStates[componentName]?.[propKey] ?? propDef.default;

    switch (propDef.type) {
      case 'string':
        return (
          <div key={propKey} className={styles.propEditor}>
            <label className={styles.propLabel}>{propDef.label}:</label>
            <input
              type="text"
              value={currentValue}
              onChange={(e) => updateProp(componentName, propKey, e.target.value)}
              className={styles.propInput}
            />
          </div>
        );

      case 'number':
        return (
          <div key={propKey} className={styles.propEditor}>
            <label className={styles.propLabel}>{propDef.label}:</label>
            <input
              type="number"
              value={currentValue}
              onChange={(e) => updateProp(componentName, propKey, Number(e.target.value))}
              min={propDef.min}
              max={propDef.max}
              className={styles.propInput}
            />
            {propDef.min !== undefined && propDef.max !== undefined && (
              <input
                type="range"
                value={currentValue}
                onChange={(e) => updateProp(componentName, propKey, Number(e.target.value))}
                min={propDef.min}
                max={propDef.max}
                className={styles.propSlider}
              />
            )}
          </div>
        );

      case 'boolean':
        return (
          <div key={propKey} className={styles.propEditor}>
            <label className={styles.propLabel}>
              <input
                type="checkbox"
                checked={currentValue}
                onChange={(e) => updateProp(componentName, propKey, e.target.checked)}
                className={styles.propCheckbox}
              />
              {propDef.label}
            </label>
          </div>
        );

      case 'enum':
        return (
          <div key={propKey} className={styles.propEditor}>
            <label className={styles.propLabel}>{propDef.label}:</label>
            <select
              value={currentValue}
              onChange={(e) => updateProp(componentName, propKey, e.target.value)}
              className={styles.propSelect}
            >
              {propDef.options.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        );

      case 'array':
        return (
          <div key={propKey} className={styles.propEditor}>
            <label className={styles.propLabel}>{propDef.label}:</label>
            <textarea
              value={JSON.stringify(currentValue, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  updateProp(componentName, propKey, parsed);
                } catch (err) {
                  // Invalid JSON, ignore
                }
              }}
              className={styles.propTextarea}
              rows={4}
            />
          </div>
        );

      case 'object':
        return (
          <div key={propKey} className={styles.propEditor}>
            <label className={styles.propLabel}>{propDef.label}:</label>
            <textarea
              value={JSON.stringify(currentValue, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  updateProp(componentName, propKey, parsed);
                } catch (err) {
                  // Invalid JSON, ignore
                }
              }}
              className={styles.propTextarea}
              rows={3}
            />
          </div>
        );

      case 'function':
        return (
          <div key={propKey} className={styles.propEditor}>
            <label className={styles.propLabel}>{propDef.label}:</label>
            <span className={styles.propFunctionNote}>(Function - click component to test)</span>
          </div>
        );

      default:
        return null;
    }
  };

  const [overlayVisible, setOverlayVisible] = useState({});

  const currentComponent = components[currentComponentIndex];
  const currentProps = componentStates[currentComponent.name] || {};

  // Prepare props for rendering, handling special cases
  const renderProps = { ...currentProps };
  
  // Handle special prop types that need conversion
  if (currentComponent.name === 'Module' && typeof renderProps.children === 'string') {
    renderProps.children = <div>{renderProps.children}</div>;
  }
  
  if (currentComponent.name === 'Overlay' && typeof renderProps.children === 'string') {
    renderProps.children = <div style={{ padding: '1rem', color: '#333' }}>{renderProps.children}</div>;
  }

  // Handle overlay components - show/hide toggle
  if (currentComponent.name === 'EventPopup') {
    renderProps.displayEventPopup = overlayVisible['EventPopup'] || currentProps.displayEventPopup;
    renderProps.closePopup = () => {
      setOverlayVisible(prev => ({ ...prev, EventPopup: false }));
      updateProp('EventPopup', 'displayEventPopup', false);
    };
  }

  if (currentComponent.name === 'WeekPopup') {
    renderProps.displayWeekPopup = overlayVisible['WeekPopup'] || currentProps.displayWeekPopup;
    renderProps.closePopup = () => {
      setOverlayVisible(prev => ({ ...prev, WeekPopup: false }));
      updateProp('WeekPopup', 'displayWeekPopup', false);
    };
  }

  // Handle function props - create wrapper functions
  Object.keys(currentComponent.props).forEach(propKey => {
    const propDef = currentComponent.props[propKey];
    if (propDef.type === 'function') {
      // Special handling for closePopup in overlays
      if ((propKey === 'closePopup' && (currentComponent.name === 'EventPopup' || currentComponent.name === 'WeekPopup'))) {
        // Already handled above
        return;
      }
      // Special handling for onChange handlers that update state
      if (propKey === 'onChange' || propKey === 'handleChange') {
        renderProps[propKey] = (...args) => {
          console.log(`[${currentComponent.name}] ${propKey} called with:`, args);
          // For onChange, update the value prop
          if (propKey === 'onChange') {
            updateProp(currentComponent.name, 'value', args[0]);
          } else if (propKey === 'handleChange') {
            // handleChange typically takes (fieldName, value) or just (value)
            if (args.length === 2) {
              // Two arguments: (fieldName, value)
              const fieldName = args[0];
              const value = args[1];
              updateProp(currentComponent.name, fieldName, value);
            } else {
              // Single argument: (value) - use fieldName prop or default to 'value'
              const fieldName = currentProps.fieldName || currentComponent.props.fieldName?.default || 'value';
              updateProp(currentComponent.name, fieldName, args[0]);
            }
          }
        };
      } else if (propKey === 'onPress') {
        // For onPress, create a wrapper that updates checked/selected/toggled if applicable
        renderProps[propKey] = () => {
          console.log(`[${currentComponent.name}] ${propKey} called`);
          // Toggle boolean props for interactive components
          if (currentComponent.name === 'ToggleSwitch' || currentComponent.name === 'Checkbox') {
            const toggleProp = currentComponent.name === 'ToggleSwitch' ? 'toggled' : 'checked';
            updateProp(currentComponent.name, toggleProp, !currentProps[toggleProp]);
          } else if (currentComponent.name === 'Button') {
            updateProp(currentComponent.name, 'selected', !currentProps.selected);
          }
        };
      } else {
        renderProps[propKey] = (...args) => {
          console.log(`[${currentComponent.name}] ${propKey} called with:`, args);
        };
      }
    }
  });

  return (
    <div className={styles.container}>
      <aside className={styles.sidebar}>
        <h1 className={styles.sidebarTitle}>Component Showcase</h1>
        <div className={styles.componentList}>
          {components.map((component, index) => (
            <button
              key={component.name}
              className={`${styles.componentListItem} ${currentComponentIndex === index ? styles.componentListItemActive : ''}`}
              onClick={() => goToIndex(index)}
            >
              {component.name}
            </button>
          ))}
        </div>
        <div className={styles.sidebarNav}>
          <button 
            onClick={() => goToIndex(currentComponentIndex - 1)}
            disabled={currentComponentIndex === 0}
            className={styles.sidebarNavButton}
          >
            ← Previous
          </button>
          <span className={styles.sidebarNavInfo}>
            {currentComponentIndex + 1} / {components.length}
          </span>
          <button 
            onClick={() => goToIndex(currentComponentIndex + 1)}
            disabled={currentComponentIndex === components.length - 1}
            className={styles.sidebarNavButton}
          >
            Next →
          </button>
        </div>
      </aside>

      <div className={styles.contentArea}>
        <div className={styles.componentSection}>
          <div className={styles.description}>
            <h2 className={styles.componentName}>{currentComponent.name}</h2>
            <p className={styles.componentDescription}>{currentComponent.description}</p>
          </div>

          <div className={styles.previewSection}>
            <div className={styles.previewContainer}>
              <div className={styles.previewLabel}>
                Preview
                {(currentComponent.name === 'EventPopup' || currentComponent.name === 'WeekPopup') && (
                  <button
                    onClick={() => {
                      const newValue = !(overlayVisible[currentComponent.name] || currentProps.displayEventPopup || currentProps.displayWeekPopup);
                      setOverlayVisible(prev => ({ ...prev, [currentComponent.name]: newValue }));
                      if (currentComponent.name === 'EventPopup') {
                        updateProp('EventPopup', 'displayEventPopup', newValue);
                      } else {
                        updateProp('WeekPopup', 'displayWeekPopup', newValue);
                      }
                    }}
                    className={styles.toggleOverlayButton}
                  >
                    {overlayVisible[currentComponent.name] || currentProps.displayEventPopup || currentProps.displayWeekPopup ? 'Hide' : 'Show'} Overlay
                  </button>
                )}
              </div>
              <div className={`${styles.previewContent} ${(currentComponent.name === 'EventPopup' || currentComponent.name === 'WeekPopup') ? styles.previewContentOverlay : ''}`}>
                {React.createElement(currentComponent.component, renderProps)}
              </div>
            </div>

            <div className={styles.controlsContainer}>
              <div className={styles.controlsLabel}>Props</div>
              <div className={styles.controlsContent}>
                {Object.keys(currentComponent.props).map(propKey => 
                  renderPropEditor(currentComponent.name, propKey, currentComponent.props[propKey])
                )}
              </div>
            </div>
          </div>

          <div className={styles.propsList}>
            <h3 className={styles.propsTitle}>Accepted Props</h3>
            <div className={styles.propsTable}>
              {Object.keys(currentComponent.props).map(propKey => {
                const prop = currentComponent.props[propKey];
                return (
                  <div key={propKey} className={styles.propRow}>
                    <div className={styles.propHeader}>
                      <span className={styles.propName}>{propKey}</span>
                      <span className={styles.propType}>({prop.type})</span>
                    </div>
                    <span className={styles.propDefault}>
                      Default: {typeof prop.default === 'object' ? JSON.stringify(prop.default) : String(prop.default)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentShowcase;

